import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from "lucide-react";
import { useContentUpload } from "@/hooks/useContentUpload";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

interface ContentUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploadComplete?: () => void;
  onContentCreated?: (contentId: string, contentType?: string) => void;
  mode?: "content" | "course";
  onCourseRequestCreated?: (requestId: string) => void;
}

export const ContentUploadDialog: React.FC<ContentUploadDialogProps> = ({
  open,
  onOpenChange,
  onUploadComplete,
  onContentCreated,
  mode = "content",
  onCourseRequestCreated,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadContent, uploading } = useContentUpload();
  const [dragActive, setDragActive] = useState(false);
  const [creatingCourse, setCreatingCourse] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const isProcessing = uploading || creatingCourse;

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const result = await uploadContent(file);

    if (result) {
      if (mode === "course" && user && onCourseRequestCreated) {
        setCreatingCourse(true);
        try {
          const { data, error } = await supabase
            .from("course_generation_requests")
            .insert({
              user_id: user.id,
              source: "upload",
              content_id: result.id,
            })
            .select()
            .single();

          if (error) throw error;

          // Fire-and-forget edge function
          supabase.functions
            .invoke("generate-course", { body: { requestId: data.id } })
            .catch((err) => console.error("Course generation invoke failed:", err));

          onOpenChange(false);
          onCourseRequestCreated(data.id);
        } catch (err: any) {
          console.error("Failed to create course request:", err);
          toast({
            title: "Error",
            description: err?.message || "Failed to start course creation",
            variant: "destructive",
          });
        } finally {
          setCreatingCourse(false);
        }
      } else {
        onOpenChange(false);
        if (onContentCreated) {
          onContentCreated(result.id, result.type);
        } else {
          onUploadComplete?.();
        }
      }
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === "course" ? "Upload Reference Material" : "Upload Content"}
          </DialogTitle>
          <DialogDescription>
            {mode === "course"
              ? "Upload a file to generate a course from its content"
              : "Upload a PDF, document, audio, or video file to get started"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-8 transition-colors ${
              dragActive
                ? "border-primary bg-primary/5"
                : "border-border bg-muted/30"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.mp3,.wav,.m4a,.mp4,.mov,.avi,.webm"
              onChange={(e) => handleFileSelect(e.target.files)}
              disabled={isProcessing}
            />

            <div className="flex flex-col items-center gap-4">
              <div className="flex items-center gap-3" />

              <div className="text-center">
                <p className="text-sm font-medium text-foreground mb-1">
                  {dragActive
                    ? "Drop your file here"
                    : "Drag and drop your file here"}
                </p>
                <p className="text-xs text-muted-foreground">or</p>
              </div>

              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessing}
                variant="outline"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {creatingCourse ? "Creating course..." : "Uploading..."}
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Browse Files
                  </>
                )}
              </Button>
            </div>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Maximum file size: 20MB
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
